import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { Check, Copy } from 'lucide-react';
import { cn, copyTextToClipboard } from '../../../lib/utils';

const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  ts: 'TypeScript',
  typescript: 'TypeScript',
  tsx: 'TypeScript',
  js: 'JavaScript',
  javascript: 'JavaScript',
  jsx: 'JavaScript',
  py: 'Python',
  python: 'Python',
  html: 'HTML',
  css: 'CSS',
  scss: 'SCSS',
  json: 'JSON',
  yaml: 'YAML',
  yml: 'YAML',
  bash: 'Bash',
  sh: 'Shell',
  shell: 'Shell',
  zsh: 'Zsh',
  sql: 'SQL',
  go: 'Go',
  rust: 'Rust',
  rs: 'Rust',
  java: 'Java',
  cpp: 'C++',
  'c++': 'C++',
  c: 'C',
  csharp: 'C#',
  'c#': 'C#',
  cs: 'C#',
  php: 'PHP',
  ruby: 'Ruby',
  rb: 'Ruby',
  swift: 'Swift',
  kotlin: 'Kotlin',
  kt: 'Kotlin',
  dart: 'Dart',
  dockerfile: 'Dockerfile',
  markdown: 'Markdown',
  md: 'Markdown',
  xml: 'XML',
  text: 'Text',
};

export function formatLanguage(language?: string): string {
  if (!language) return 'Code';
  const lower = language.toLowerCase();
  if (LANGUAGE_DISPLAY_NAMES[lower]) {
    return LANGUAGE_DISPLAY_NAMES[lower];
  }
  return language.charAt(0).toUpperCase() + language.slice(1);
}

export function extractCodeText(children: unknown): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map((c) => (typeof c === 'string' ? c : extractCodeText(c))).join('');
  }
  if (children && typeof children === 'object' && 'props' in (children as any)) {
    return extractCodeText((children as any).props?.children);
  }
  return String(children ?? '');
}

const CodeBracketIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="7 7 2 12 7 17" />
    <polyline points="17 17 22 12 17 7" />
    <line x1="14" x2="10" y1="5" y2="19" />
  </svg>
);

export interface ChatCodeBlockProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

export const ChatCodeBlock = ({
  inline,
  className,
  children,
  ...props
}: ChatCodeBlockProps) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const codeText = extractCodeText(children).replace(/\n$/, '');
  const isBlock = !inline && (Boolean(match) || codeText.includes('\n'));

  if (isBlock) {
    const rawLanguage =
      match?.[1] ?? (/\bimport\s+pandas\b/i.test(codeText) ? 'python' : '');
    const displayLanguage = formatLanguage(rawLanguage);

    const handleCopy = async () => {
      try {
        await copyTextToClipboard(codeText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy code snippet', err);
      }
    };

    return (
      <div className="relative my-3 overflow-hidden rounded-2xl border border-white/5 bg-[#1e1e20] text-gray-200 shadow-sm">
        <div className="flex select-none items-center justify-between px-4 pt-3.5 pb-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-white/90 dark:text-true-white">
            <CodeBracketIcon className="h-4 w-4 text-white/80 dark:text-gray-300" />
            <span>{displayLanguage}</span>
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="flex cursor-pointer items-center justify-center rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white/10 hover:text-white dark:hover:text-true-white"
            title={copied ? 'Copied' : 'Copy code'}
            aria-label={copied ? 'Copied' : 'Copy code'}
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-400" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
        <SyntaxHighlighter
          style={dracula}
          language={rawLanguage || 'text'}
          PreTag="div"
          customStyle={{
            backgroundColor: 'transparent',
            margin: 0,
            padding: '0.25rem 1rem 1rem 1rem',
            overflowX: 'auto',
            fontSize: '0.875rem',
            lineHeight: '1.6',
          }}
          {...props}
        >
          {codeText}
        </SyntaxHighlighter>
      </div>
    );
  }

  return (
    <code
      className={cn(
        'rounded-[7px] border border-gray-300/60 bg-[#f0f1f3] px-1.5 py-0.5 font-mono text-sm text-[#ab5152] dark:border-gray-600 dark:bg-muted dark:text-[#fca5a5]',
        className
      )}
      {...props}
    >
      {codeText}
    </code>
  );
};
